import { faArrowRotateLeft, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Text, TextButton, Tooltip } from '@modusign/design-system';
import { ReactNode } from 'react';

import { ResourceRoleSubject } from '@app/features/authorization/types';
import isEqualResourceRoleSubject from '@app/features/authorization/utils/isEqualResourceRole';
import useDebouncedState from '@app/hooks/useDebouncedState';

import SearchedMemberGroupTab from '../../SearchedMemberGroupTab';
import SelectedMemberGroupArea from './SelectedMemberGroupArea';

export type ResourcePermissionAssignOption = 'readOnly' | 'readAndExport';

interface AddAssigneeFormProps {
  children?: ReactNode;
  disabled?: boolean;
  nonAssignables?: ResourceRoleSubject[];
  preGrantedResourceRoles?: ResourceRoleSubject[];
  subjects: ResourceRoleSubject[];
  tooltipContent?: ReactNode;
  onChange: (value: ResourceRoleSubject[]) => void;
}

function AddAssigneeForm({
  children,
  disabled,
  nonAssignables,
  preGrantedResourceRoles,
  subjects,
  tooltipContent,
  onChange,
}: AddAssigneeFormProps) {
  const [debouncedValue, actualValue, setActualValue] = useDebouncedState('', 500);

  return (
    <Flex flexDirection="column">
      <Flex alignItems="center" justifyContent="space-between" mb={7}>
        <Text fontSize="md" fontWeight="medium">
          선택한 멤버 · 그룹
        </Text>
        {children}
      </Flex>
      <SelectedMemberGroupArea
        keyword={actualValue}
        selectedResourceRoleSubjects={subjects}
        onKeywordChange={setActualValue}
        onSelectedResourceRoleSubjectsChange={onChange}
      />
      <Flex flexDirection="column" gap={4} mt={7}>
        {debouncedValue ? (
          <Flex justifyContent="space-between">
            <Text fontWeight="medium">{`'${debouncedValue}' 검색 결과`}</Text>
            <TextButton onClick={() => setActualValue('')}>
              <Box as={FontAwesomeIcon} icon={faArrowRotateLeft} />
              <Text ml="2px">검색 초기화</Text>
            </TextButton>
          </Flex>
        ) : (
          <Text fontSize="md" fontWeight="medium">
            {nonAssignables ? (
              <>
                추가 가능 멤버 · 그룹
                <Tooltip
                  content={
                    <Text fontSize="sm">
                      자동 공유가 설정되어 있는 경우
                      <br />
                      해당 멤버·그룹은 추가할 수 없습니다.
                    </Text>
                  }
                  scale="md"
                >
                  <Flex
                    alignItems="center"
                    display="inline-flex"
                    height="20px"
                    justifyContent="center"
                    width="20px"
                  >
                    <Box
                      as={FontAwesomeIcon}
                      color="iconNeutralDark"
                      height="12px"
                      icon={faQuestionCircle}
                      width="12px"
                    />
                  </Flex>
                </Tooltip>
              </>
            ) : (
              '전체 멤버 · 그룹'
            )}
          </Text>
        )}
        <SearchedMemberGroupTab
          disabled={disabled}
          hiddenSubjects={nonAssignables}
          keyword={debouncedValue}
          selectedSubjects={subjects}
          tooltipContent={tooltipContent}
          unavailableSubjects={preGrantedResourceRoles}
          onToggle={(v) =>
            onChange(
              subjects.some((s) => isEqualResourceRoleSubject(s, v))
                ? subjects.filter((s) => !isEqualResourceRoleSubject(s, v))
                : [...subjects, v]
            )
          }
        />
      </Flex>
    </Flex>
  );
}

export default AddAssigneeForm;
